import type { Entity } from '@backstage/catalog-model';
import type {
  TopologyGraph,
  DomainGroup,
  SystemGroup,
  TopologyNode,
  TopologyEdge,
  DependencyType,
} from '../types';

interface MinimalCatalogApi {
  getEntities(request: { filter: Record<string, string> }): Promise<{ items: Entity[] }>;
}

/** Excludes hand-authored catalog-info.yaml entities (e.g. docs) that never came from the System Registry sync. */
function isFromSystemRegistry(entity: Entity): boolean {
  return entity.metadata.annotations?.['verint.com/source'] === 'system-registry';
}

export async function fetchTopologyFromCatalog(
  catalogApi: MinimalCatalogApi,
  filter?: string,
): Promise<TopologyGraph> {
  // Fetch all system-registry entities from the catalog
  const [systemsRaw, componentsRaw, resourcesRaw, apisRaw] = await Promise.all([
    catalogApi.getEntities({ filter: { kind: 'System' } }),
    catalogApi.getEntities({ filter: { kind: 'Component' } }),
    catalogApi.getEntities({ filter: { kind: 'Resource' } }),
    catalogApi.getEntities({ filter: { kind: 'API' } }),
  ]);
  const systemsRes = { items: systemsRaw.items.filter(isFromSystemRegistry) };
  const componentsRes = { items: componentsRaw.items.filter(isFromSystemRegistry) };
  const resourcesRes = { items: resourcesRaw.items.filter(isFromSystemRegistry) };
  const apisRes = { items: apisRaw.items.filter(isFromSystemRegistry) };

  const filterLower = filter?.toLowerCase();

  // Match filter against domain, system name/title, region, CSP, or env
  const systems = systemsRes.items.filter(e => {
    if (!filterLower) return true;
    const domain = getDomain(e).toLowerCase();
    const name = e.metadata.name.toLowerCase();
    const title = (e.metadata.title || '').toLowerCase();
    const ann = (e.metadata.annotations ?? {}) as Record<string, string>;
    const labels = (e.metadata.labels ?? {}) as Record<string, string>;
    const region = (ann['verint.com/region'] || '').toLowerCase();
    const csp = (ann['verint.com/csp'] || '').toLowerCase();
    const env = (labels['verint.com/env'] || '').toLowerCase();
    const customers = parseCustomers(ann['verint.com/customers']).map(c => c.toLowerCase());
    return domain === filterLower || name === filterLower || title.includes(filterLower)
      || region === filterLower || csp === filterLower || env === filterLower
      || customers.includes(filterLower);
  });

  const domainMap = new Map<string, SystemGroup[]>();

  for (const sys of systems) {
    const domain = getDomain(sys) || 'default';
    const sysName = sys.metadata.name;
    const sysNs = sys.metadata.namespace || 'default';
    const sysRef = `system:${sysNs}/${sysName}`;

    const components: TopologyNode[] = componentsRes.items
      .filter(c => {
        const s = (c.spec as any)?.system;
        return s === sysRef || s === `system:default/${sysName}` || s === sysName;
      })
      .map(c => ({
        key: c.metadata.name,
        displayName: c.metadata.title || c.metadata.name,
        type: (c.spec as any)?.type || 'service',
        nodeKind: 'component' as const,
        namespace: c.metadata.namespace || 'default',
      }));

    const resources: TopologyNode[] = resourcesRes.items
      .filter(r => {
        const s = (r.spec as any)?.system;
        return s === sysRef || s === `system:default/${sysName}` || s === sysName;
      })
      .map(r => ({
        key: r.metadata.name,
        displayName: r.metadata.title || r.metadata.name,
        type: (r.spec as any)?.type || 'database',
        nodeKind: 'resource' as const,
        namespace: r.metadata.namespace || 'default',
      }));

    const apis: TopologyNode[] = apisRes.items
      .filter(a => {
        const s = (a.spec as any)?.system;
        return s === sysRef || s === `system:default/${sysName}` || s === sysName;
      })
      .map(a => ({
        key: a.metadata.name,
        displayName: a.metadata.title || a.metadata.name,
        type: (a.spec as any)?.type || 'openapi',
        nodeKind: 'api' as const,
        namespace: a.metadata.namespace || 'default',
      }));

    // Include systems even with no children (show as leaf nodes)

    // Extract region: prefer direct annotation, fall back to metadata JSON
    let region = sys.metadata.annotations?.['verint.com/region'] || undefined;
    if (!region) {
      try {
        const metaStr = sys.metadata.annotations?.['verint.com/metadata'];
        if (metaStr) {
          const meta = JSON.parse(metaStr);
          region = meta.region || undefined;
        }
      } catch { /* ignore parse errors */ }
    }

    const customers = parseCustomers(sys.metadata.annotations?.['verint.com/customers']);

    const group: SystemGroup = {
      key: sysName,
      domain,
      displayName: sys.metadata.title || sysName,
      owner: (sys.spec as any)?.owner || '',
      team: sys.metadata.annotations?.['verint.com/team'] || '',
      region,
      namespace: sysNs,
      ...(customers.length ? { customers } : {}),
      components,
      resources,
      apis,
    };

    const existing = domainMap.get(domain) ?? [];
    existing.push(group);
    domainMap.set(domain, existing);
  }

  const domains: DomainGroup[] = Array.from(domainMap.entries()).map(
    ([name, sysGroups]) => ({ name, systems: sysGroups }),
  );

  // Extract dependencies: system-level dependsOn + component-level dependsOn/providesApi/consumesApi
  const edges: TopologyEdge[] = [];
  const edgeSet = new Set<string>();

  const addEdge = (
    src: string,
    tgt: string,
    type: DependencyType = 'critical',
    severity?: string,
  ) => {
    const key = `${src}::${tgt}`;
    if (edgeSet.has(key)) return;
    edgeSet.add(key);
    edges.push({
      sourceSystem: src,
      targetSystem: tgt,
      dependencyType: type,
      ...(type === 'noncritical' && severity ? { severity } : {}),
    });
  };

  // System-level dependsOn
  for (const sys of systems) {
    const dependsOn = (sys.spec as any)?.dependsOn as string[] | undefined;
    if (!dependsOn) continue;
    const criticalities = parseAnnotationMap(sys, 'verint.com/dependency-criticality');
    const severities = parseAnnotationMap(sys, 'verint.com/dependency-severities');
    for (const dep of dependsOn) {
      const targetName = dep.replace(/^system:(default\/)?/, '');
      const { dependencyType, severity } = gradeDependency(
        criticalities[dep],
        severities[dep],
      );
      addEdge(
        `${getDomain(sys)}-${sys.metadata.name}`,
        targetName,
        dependencyType,
        severity,
      );
    }
  }

  // Component-level dependsOn (component→component or component→resource cross-system)
  for (const comp of componentsRes.items) {
    const compSys = (comp.spec as any)?.system;
    if (!compSys) continue;
    const srcSysName = compSys.replace(/^system:(default\/)?/, '');

    const dependsOn = (comp.spec as any)?.dependsOn as string[] | undefined;
    if (!dependsOn) continue;
    for (const dep of dependsOn) {
      // Find which system the target belongs to
      const targetName = dep.replace(/^(component|resource):(default\/)?/, '');
      const targetEntity = [...componentsRes.items, ...resourcesRes.items].find(
        e => e.metadata.name === targetName,
      );
      if (!targetEntity) continue;
      const tgtSys = (targetEntity.spec as any)?.system;
      if (!tgtSys) continue;
      const tgtSysName = tgtSys.replace(/^system:(default\/)?/, '');
      if (srcSysName !== tgtSysName) {
        // Find domains for proper edge IDs
        const srcSysEntity = systems.find(s => s.metadata.name === srcSysName);
        const tgtSysEntity = systems.find(s => s.metadata.name === tgtSysName);
        if (srcSysEntity && tgtSysEntity) {
          addEdge(
            `${getDomain(srcSysEntity)}-${srcSysName}`,
            `${getDomain(tgtSysEntity)}-${tgtSysName}`,
          );
        }
      }
    }
  }

  // API relationships: consumesApi/providesApi cross-system
  for (const comp of componentsRes.items) {
    const compSys = (comp.spec as any)?.system;
    if (!compSys) continue;
    const srcSysName = compSys.replace(/^system:(default\/)?/, '');

    const consumesApi = (comp.spec as any)?.consumesApis as string[] | undefined;
    if (!consumesApi) continue;
    for (const apiRef of consumesApi) {
      const apiName = apiRef.replace(/^api:(default\/)?/, '');
      const apiEntity = apisRes.items.find(a => a.metadata.name === apiName);
      if (!apiEntity) continue;
      const apiSys = (apiEntity.spec as any)?.system;
      if (!apiSys) continue;
      const tgtSysName = apiSys.replace(/^system:(default\/)?/, '');
      if (srcSysName !== tgtSysName) {
        const srcSysEntity = systems.find(s => s.metadata.name === srcSysName);
        const tgtSysEntity = systems.find(s => s.metadata.name === tgtSysName);
        if (srcSysEntity && tgtSysEntity) {
          addEdge(
            `${getDomain(srcSysEntity)}-${srcSysName}`,
            `${getDomain(tgtSysEntity)}-${tgtSysName}`,
            'optional',
          );
        }
      }
    }
  }

  return { domains, edges };
}

function getDomain(entity: Entity): string {
  return (
    entity.metadata.annotations?.['verint.com/domain'] ||
    (entity.spec as any)?.domain ||
    entity.metadata.labels?.['verint.com/domain'] ||
    'default'
  );
}

function parseCustomers(raw: string | undefined): string[] {
  return (raw ?? '').split(',').map(c => c.trim()).filter(Boolean);
}

const UNASSIGNED_CUSTOMER_GROUP = 'Unassigned';

/**
 * Regroups an already-built topology by customer instead of domain.
 * A system's own `domain` field (and therefore its node id, via `composeSystemId`)
 * is untouched — only the top-level grouping changes, so edges keep resolving.
 * Systems with no customers land in "Unassigned"; systems serving more than one
 * customer get their own combined bucket rather than being duplicated across
 * groups (ELK's hierarchical layout requires each node to have a single parent).
 */
export function groupByCustomer(graph: TopologyGraph): TopologyGraph {
  const groupMap = new Map<string, SystemGroup[]>();
  for (const domain of graph.domains) {
    for (const system of domain.systems) {
      const customers = system.customers ?? [];
      const groupName = customers.length
        ? Array.from(new Set(customers)).sort().join(' + ')
        : UNASSIGNED_CUSTOMER_GROUP;
      const existing = groupMap.get(groupName) ?? [];
      existing.push(system);
      groupMap.set(groupName, existing);
    }
  }

  const domains: DomainGroup[] = Array.from(groupMap.entries())
    .sort(([a], [b]) => (a === UNASSIGNED_CUSTOMER_GROUP ? 1 : b === UNASSIGNED_CUSTOMER_GROUP ? -1 : a.localeCompare(b)))
    .map(([name, systems]) => ({ name, systems }));

  return { domains, edges: graph.edges };
}

function parseAnnotationMap(entity: Entity, annotation: string): Record<string, string> {
  const raw = entity.metadata.annotations?.[annotation];
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Feeds predating the `type` field encoded criticality in `severity`. */
function gradeDependency(
  criticality?: string,
  severity?: string,
): { dependencyType: DependencyType; severity?: string } {
  const c = (criticality ?? '').toLowerCase().replace(/[^a-z]/g, '');
  const s = (severity ?? '').toLowerCase();
  if (c === 'critical' || (!c && s === 'critical')) {
    return { dependencyType: 'critical' };
  }
  if (c === 'noncritical') {
    return { dependencyType: 'noncritical', severity: s === 'unknown' ? undefined : s || undefined };
  }
  if (!s || s === 'unknown') return { dependencyType: 'unknown' };
  return { dependencyType: 'noncritical', severity: s };
}
